import { Card } from '../components/ui/card';
import { ArrowLeft, TrendingUp, TrendingDown, CheckCircle2 } from 'lucide-react';
import { EnhancedShareButton } from '../components/EnhancedShareButton';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { useNavigate } from 'react-router';

export default function WeeklySummary() {
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { accessToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      // TEMPORARY: Generate summary from local storage
      const storedCheckins = localStorage.getItem('tension_checkins');
      const checkins = storedCheckins ? JSON.parse(storedCheckins) : [];
      
      if (checkins.length >= 3) {
        // Calculate summary statistics
        const scores = checkins.map((c: any) => c.rtiScore || 0);
        const avgScore = Math.round(scores.reduce((a: number, b: number) => a + b, 0) / scores.length);
        
        const stableDays = scores.filter((s: number) => s <= 35).length;
        const elasticDays = scores.filter((s: number) => s > 35 && s <= 60).length;
        const highTensionDays = scores.filter((s: number) => s > 60).length;
        
        // Determine pattern
        let pattern = '';
        if (stableDays > checkins.length / 2) {
          pattern = 'Sustained Equilibrium: You maintain consistent low tension';
        } else if (highTensionDays > elasticDays) {
          pattern = 'Elevated Pursuit Pattern: High initiation creating distance';
        } else if (elasticDays > stableDays) {
          pattern = 'Fluctuating Tension: Minor oscillations between states';
        } else {
          pattern = 'Recovery Pattern: Moving from high tension toward stability';
        }
        
        const completedActions = localStorage.getItem('tension_action_completed') ? 1 : 0;
        const totalActions = checkins.length;
        
        setSummary({
          pattern,
          avgScore,
          completedActions,
          totalActions,
          stableDays,
          highTensionDays,
        });
      }
      
      // Original server code (commented out due to deployment issue)
      /*
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/weekly-summary`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const data = await response.json();
      setSummary(data.summary);
      */
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-neutral-600">Loading...</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="min-h-screen tension-page-bg">
        <div className="bg-white border-b border-neutral-200 px-6 py-4">
          <div className="max-w-2xl mx-auto flex items-center gap-4">
            <button
              onClick={() => navigate('/home')}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-neutral-600" />
            </button>
            <h1 className="text-xl font-light">Weekly Summary</h1>
          </div>
        </div>
        <div className="max-w-2xl mx-auto px-6 py-12">
          <Card className="p-8 text-center">
            <p className="text-neutral-600">Not enough data yet. Complete more check-ins to see your weekly summary.</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/home')}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-600" />
          </button>
          <h1 className="text-xl font-light">Weekly Summary</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12 space-y-6">
        {/* Pattern Card */}
        <Card className="p-8 bg-neutral-900 text-white">
          <div className="space-y-4">
            <p className="text-sm text-neutral-400 uppercase tracking-wider">Pattern Detected</p>
            <h2 className="text-2xl font-light">{summary.pattern}</h2>
          </div>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="p-6">
            <div className="space-y-2">
              <p className="text-sm text-neutral-600">Average score</p>
              <p className="text-3xl font-light text-neutral-900">{summary.avgScore}</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="space-y-2">
              <p className="text-sm text-neutral-600">Actions Completed</p>
              <p className="text-3xl font-light text-neutral-900">
                {summary.completedActions}/{summary.totalActions}
              </p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <p className="text-sm text-neutral-600">Stable Days</p>
              </div>
              <p className="text-3xl font-light text-neutral-900">{summary.stableDays}</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <p className="text-sm text-neutral-600">High Tension Days</p>
              </div>
              <p className="text-3xl font-light text-neutral-900">{summary.highTensionDays}</p>
            </div>
          </Card>
        </div>

        {/* What Helped */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-medium">What Helped</h3>
            </div>
            <ul className="space-y-2 text-sm text-neutral-700">
              {summary.stableDays > 3 && (
                <li>• Maintained stable equilibrium for multiple days</li>
              )}
              {summary.completedActions > 0 && (
                <li>• Followed {summary.completedActions} action recommendations</li>
              )}
              {summary.avgScore < 50 && (
                <li>• Kept average tension below elastic threshold</li>
              )}
            </ul>
          </div>
        </Card>

        {/* What Increased Tension */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-medium">What Increased Tension</h3>
            </div>
            <ul className="space-y-2 text-sm text-neutral-700">
              {summary.highTensionDays > 0 && (
                <li>• {summary.highTensionDays} day(s) of elevated tension</li>
              )}
              {summary.avgScore > 60 && (
                <li>• Average tension in high zone - consider restraint strategies</li>
              )}
              {summary.completedActions < summary.totalActions / 2 && (
                <li>• Incomplete action adherence may have contributed to pressure</li>
              )}
            </ul>
          </div>
        </Card>

        {/* Insight */}
        <Card className="p-6 bg-neutral-50 border-neutral-200">
          <p className="text-sm text-neutral-700 text-center">
            {summary.avgScore <= 35 && 'Patterns are forming. Awareness is already changing outcomes.'}
            {summary.avgScore > 35 && summary.avgScore <= 60 && 'You now recognize tension before it becomes distance. Keep observing.'}
            {summary.avgScore > 60 && "You're learning restraint. That's a skill most people never master."}
          </p>
        </Card>

        {/* Share Button - Static Embedded */}
        <EnhancedShareButton variant="prominent" />
      </div>
    </div>
  );
}